import UserService from "./UserService";
import ConfigurationService from "./ConfigurationService";
import Kichiri from "./KichiriService";

const BankReconciliationService = {
	/**
	 * Fetches available QBO bank accounts for a client.
	 *
	 * @param {Object} params
	 * @param {number} params.clientId
	 * @returns {Promise<Array>} Array of bank account objects
	 */
	async fetchBankAccounts({ clientId }) {
		let userData = UserService.getUserData();

		try {
			let response = await Kichiri.tools.bankReconAccounts({}, { clientId }, userData.authToken);
			return response.data.accounts || [];
		} catch (error) {
			console.error("Error fetching bank accounts:", error);
			return [];
		}
	},

	/**
	 * Runs a bank reconciliation for a client's bank account.
	 * Sends the bank statement PDF as a multipart file upload.
	 *
	 * @param {Object} params
	 * @param {number} params.clientId
	 * @param {number} params.accountId
	 * @param {string} params.accountName
	 * @param {string} params.startDate
	 * @param {string} params.endDate
	 * @param {File} params.file - Bank statement PDF file
	 * @param {number} params.bankStatementEndingBalance - Optional manual ending balance
	 * @returns {Promise<Object>} Reconciliation result
	 */
	async runReconciliation({ clientId, accountId, accountName, startDate, endDate, file, bankStatementEndingBalance, source, plaidAccountId }) {
		let userData = UserService.getUserData();

		try {
			let body;
			let headers = { authorization: userData.authToken };

			if (source === "plaid") {
				// Plaid source: send JSON (no file upload)
				body = JSON.stringify({ clientId, accountId, accountName, startDate, endDate, source: "plaid", plaidAccountId });
				headers["Content-Type"] = "application/json";
				headers["Accept"] = "application/json";
			} else {
				// PDF source: send multipart form data — kichiri doesn't support multipart, stays on raw fetch.
				let formData = new FormData();
				formData.append("files", file, file.name);
				formData.append("clientId", clientId);
				formData.append("accountId", accountId);
				formData.append("accountName", accountName);
				formData.append("startDate", startDate);
				formData.append("endDate", endDate);
				if (bankStatementEndingBalance != null) {
					formData.append("bankStatementEndingBalance", bankStatementEndingBalance);
				}
				body = formData;
			}

			let response = await fetch(`${ConfigurationService.resolveApi().rest}/tools/bank_reconciliation/run`, {
				method: "POST",
				body,
				headers
			});

			if (response.status !== 200) {
				let errorData = await response.json();
				return { error: errorData.error || "Failed to run reconciliation" };
			}

			let data = await response.json();
			return data;
		} catch (error) {
			console.error("Error running reconciliation:", error);
			return { error: "Failed to run reconciliation" };
		}
	},

	/**
	 * Lists reconciliation history for a client.
	 *
	 * @param {Object} params
	 * @param {number} params.clientId
	 * @returns {Promise<Array>}
	 */
	async listReconciliations({ clientId }) {
		let userData = UserService.getUserData();

		try {
			let response = await Kichiri.tools.bankReconList({}, { clientId }, userData.authToken);
			return response.data.reconciliations || [];
		} catch (error) {
			console.error("Error listing reconciliations:", error);
			return [];
		}
	},

	/**
	 * Gets a reconciliation by ID.
	 *
	 * @param {Object} params
	 * @param {number} params.reconciliationId
	 * @returns {Promise<Object>}
	 */
	async getReconciliation({ reconciliationId }) {
		let userData = UserService.getUserData();

		try {
			let response = await Kichiri.tools.bankReconGet({}, { reconciliationId }, userData.authToken);
			return response.data.reconciliation;
		} catch (error) {
			console.error("Error fetching reconciliation:", error);
			return null;
		}
	},

	/**
	 * Downloads a bank reconciliation as an Excel file.
	 * Stays on raw fetch — kichiri returns parsed JSON, but the endpoint streams a binary blob.
	 *
	 * @param {Object} params
	 * @param {number} params.reconciliationId
	 * @param {string} params.clientName
	 * @param {string} params.periodEnd
	 */
	async downloadReconciliation({ reconciliationId, clientName, periodEnd }) {
		let userData = UserService.getUserData();

		try {
			let response = await fetch(`${ConfigurationService.resolveApi().rest}/tools/bank_reconciliation/download`, {
				method: "POST",
				body: JSON.stringify({ reconciliationId }),
				headers: {
					authorization: userData.authToken,
					Accept: "application/json",
					"Content-Type": "application/json"
				}
			});

			let blob = await response.blob();

			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.target = "_blank";
			a.download = `${clientName || "Client"} - Bank Reconciliation - ${periodEnd || "report"}.xlsx`;
			document.body.appendChild(a);
			a.click();
			a.remove();
			window.URL.revokeObjectURL(url);
		} catch (error) {
			console.error("Error downloading reconciliation:", error);
		}
	},

	/**
	 * Updates a reconciliation item's status.
	 *
	 * @param {Object} params
	 * @param {number} params.itemId
	 * @param {string} params.matchStatus
	 * @param {string} params.category
	 * @returns {Promise<Object>}
	 */
	async updateItem({ itemId, matchStatus, category }) {
		let userData = UserService.getUserData();

		try {
			let response = await Kichiri.tools.bankReconUpdateItem(
				{ itemId, matchStatus, category },
				{},
				userData.authToken
			);
			return response.data.item;
		} catch (error) {
			console.error("Error updating item:", error);
			return null;
		}
	},

	/**
	 * Updates an adjustment's status (accept/reject).
	 *
	 * @param {Object} params
	 * @param {number} params.adjustmentId
	 * @param {string} params.status - "accepted" or "rejected"
	 * @returns {Promise<Object>}
	 */
	async updateAdjustmentStatus({ adjustmentId, status, memo }) {
		let userData = UserService.getUserData();

		try {
			let body = { adjustmentId };
			if (status) body.status = status;
			if (memo !== undefined) body.memo = memo;

			let response = await Kichiri.tools.bankReconUpdateAdjustment(body, {}, userData.authToken);
			return response.data.adjustment;
		} catch (error) {
			console.error("Error updating adjustment:", error);
			return { error: "Failed to update adjustment" };
		}
	},

	/**
	 * Posts an accepted adjustment as a journal entry to QBO.
	 *
	 * @param {Object} params
	 * @param {number} params.adjustmentId
	 * @param {number} params.clientId
	 * @returns {Promise<Object>}
	 */
	async postAdjustmentToQBO({ adjustmentId, clientId }) {
		let userData = UserService.getUserData();

		try {
			let response = await Kichiri.tools.bankReconPostAdjustment(
				{ adjustmentId, clientId },
				{},
				userData.authToken
			);
			return response.data;
		} catch (error) {
			console.error("Error posting adjustment to QBO:", error);
			let errorMessage =
				error?.response?.data?.error || "Failed to post journal entry to QBO";
			return { error: errorMessage };
		}
	},

	/**
	 * Lists reconciliations for all clients in a given month.
	 *
	 * @param {Object} params
	 * @param {number} params.year
	 * @param {number} params.month
	 * @returns {Promise<Array>}
	 */
	async listAllByMonth({ year, month }) {
		let userData = UserService.getUserData();

		try {
			let response = await Kichiri.tools.bankReconListAll({}, { year, month }, userData.authToken);
			return response.data.reconciliations || [];
		} catch (error) {
			console.error("Error fetching all reconciliations:", error);
			return [];
		}
	},

	async fetchDataQualityAlerts({ clientId }) {
		let userData = UserService.getUserData();

		try {
			let response = await Kichiri.tools.bankReconDataQualityAlerts(
				{},
				{ clientId },
				userData.authToken
			);
			return response.data.alerts || [];
		} catch (error) {
			console.error("Error fetching data quality alerts:", error);
			return [];
		}
	},

	async acknowledgeDataQualityAlert({ alertId, action }) {
		let userData = UserService.getUserData();

		try {
			let response = await Kichiri.tools.bankReconAcknowledgeDataQualityAlert(
				{ alertId, action },
				{},
				userData.authToken
			);
			return response.data.alert;
		} catch (error) {
			console.error("Error acknowledging alert:", error);
			return { error: "Failed to update alert" };
		}
	},

	async saveDuplicateDecision({ clientId, plaidAccountId, transactionId, pairTransactionId, decision, groupId }) {
		let userData = UserService.getUserData();

		try {
			let response = await Kichiri.tools.bankReconSaveDuplicateDecision(
				{ clientId, plaidAccountId, transactionId, pairTransactionId, decision, groupId },
				{},
				userData.authToken
			);
			return response.data.decision;
		} catch (error) {
			console.error("Error saving duplicate decision:", error);
			return { error: "Failed to save decision" };
		}
	},

	async saveAutoReconSettings({ clientId, autoReconcile, plaidAccountId, qboAccountId }) {
		let userData = UserService.getUserData();

		try {
			let response = await Kichiri.tools.bankReconAutoReconSettings(
				{ clientId, autoReconcile, plaidAccountId, qboAccountId },
				{},
				userData.authToken
			);
			return response.data;
		} catch (error) {
			console.error("Error saving auto-recon settings:", error);
			return { error: "Failed to save settings" };
		}
	}
};

export default BankReconciliationService;
